import { PDFDocument } from "pdf-lib";
import * as pdfjs from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.mjs?url";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export type PdfDocument = pdfjs.PDFDocumentProxy;

export type PdfRenderResult = {
  pageCount: number;
  viewportWidth: number;
  viewportHeight: number;
  pdfWidth: number;
  pdfHeight: number;
};

export type SignaturePlacement = {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  viewportWidth: number;
  viewportHeight: number;
};

export async function loadPdfDocument(bytes: ArrayBuffer): Promise<PdfDocument> {
  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(bytes) });
  return loadingTask.promise;
}

export async function renderPdfDocumentPage(
  document: PdfDocument,
  canvas: HTMLCanvasElement,
  pageNumber: number,
  maxWidth: number,
  signal?: AbortSignal,
): Promise<PdfRenderResult> {
  if (signal?.aborted) {
    throw new Error("PDF render aborted");
  }

  const safePageNumber = clampPageNumber(pageNumber, document.numPages);
  const page = await document.getPage(safePageNumber);
  const unscaledViewport = page.getViewport({ scale: 1 });
  const scale = Math.min(maxWidth / unscaledViewport.width, 1.6);
  const viewport = page.getViewport({ scale });
  const pixelRatio = window.devicePixelRatio || 1;
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Canvas context is unavailable");
  }

  canvas.width = Math.floor(viewport.width * pixelRatio);
  canvas.height = Math.floor(viewport.height * pixelRatio);
  canvas.style.width = `${Math.floor(viewport.width)}px`;
  canvas.style.height = `${Math.floor(viewport.height)}px`;
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  context.clearRect(0, 0, viewport.width, viewport.height);

  const renderTask = page.render({ canvasContext: context, viewport });
  const cancelRender = () => renderTask.cancel();
  signal?.addEventListener("abort", cancelRender, { once: true });

  try {
    await renderTask.promise;
  } catch (error) {
    if (signal?.aborted || isRenderCancellation(error)) {
      throw new Error("PDF render aborted");
    }
    throw error;
  } finally {
    signal?.removeEventListener("abort", cancelRender);
  }

  return {
    pageCount: document.numPages,
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
    pdfWidth: unscaledViewport.width,
    pdfHeight: unscaledViewport.height,
  };
}

export async function renderPdfPage(
  bytes: ArrayBuffer,
  canvas: HTMLCanvasElement,
  pageNumber: number,
  maxWidth: number,
): Promise<PdfRenderResult> {
  const document = await loadPdfDocument(bytes);
  try {
    return await renderPdfDocumentPage(document, canvas, pageNumber, maxWidth);
  } finally {
    await document.destroy();
  }
}

export async function signPdf(
  pdfBytes: ArrayBuffer,
  signaturePngBytes: ArrayBuffer,
  placement: SignaturePlacement,
): Promise<Blob> {
  const pdf = await PDFDocument.load(pdfBytes);
  const pages = pdf.getPages();
  const page = pages[Math.max(0, Math.min(placement.pageNumber - 1, pages.length - 1))];
  const signature = await pdf.embedPng(signaturePngBytes);
  const pageWidth = page.getWidth();
  const pageHeight = page.getHeight();
  const x = (placement.x / placement.viewportWidth) * pageWidth;
  const y = pageHeight - ((placement.y + placement.height) / placement.viewportHeight) * pageHeight;
  const width = (placement.width / placement.viewportWidth) * pageWidth;
  const height = (placement.height / placement.viewportHeight) * pageHeight;

  page.drawImage(signature, { x, y, width, height });

  const output = await pdf.save();
  const arrayBuffer = output.buffer.slice(output.byteOffset, output.byteOffset + output.byteLength) as ArrayBuffer;
  return new Blob([arrayBuffer], { type: "application/pdf" });
}

function clampPageNumber(pageNumber: number, pageCount: number): number {
  if (!Number.isFinite(pageNumber)) return 1;
  return Math.max(1, Math.min(Math.trunc(pageNumber), Math.max(1, pageCount)));
}

function isRenderCancellation(error: unknown): boolean {
  return error instanceof Error && error.name === "RenderingCancelledException";
}
